/**
 * Browse a CARA connection. Rendered inside `ConnectionBrowserPage`, which owns
 * the page header and the connection picker; this component is handed the
 * chosen `connectionId` and does nothing with routing.
 *
 * One tab's worth of surface, because CARA is a read-only source here: pick an
 * object type (or write the search body), search, page through the results, and
 * export what you see to CSV. There is no upload, no delete and no document
 * download — not "not yet", but by construction (ADR-0023).
 *
 * Paging is offset-based because that is what CARA's search takes, and the
 * total it reports is what tells you whether there is another page.
 */

import { useCallback, useState } from "react";
import { Alert, Badge, Button, Group, Loader, Paper, Stack, Table, Text } from "@mantine/core";
import {
  IconChevronLeft,
  IconChevronRight,
  IconSearch,
  IconTableExport,
} from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { downloadCsv } from "@/shared/api/integration-browse";
import { caraSearch, type CaraSearchResult } from "@/shared/api/integration-cara";
import { CaraSearchBuilder } from "./CaraSearchBuilder";
import { caraSearchReady, EMPTY_CARA_SEARCH, type CaraSearchSelection } from "./caraSearch";

const PAGE_SIZE = 100;

function cell(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

export function CaraBrowser({ connectionId }: { connectionId: string }) {
  const getToken = useAccessToken();
  const [search, setSearch] = useState<CaraSearchSelection>(EMPTY_CARA_SEARCH);
  const [result, setResult] = useState<CaraSearchResult | null>(null);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ready = caraSearchReady(search);

  const run = useCallback(
    async (from: number) => {
      if (!ready) return;
      setLoading(true);
      setError(null);
      try {
        const token = await getToken();
        const found = await caraSearch(token, connectionId, {
          typeName: search.mode === "builder" ? search.typeName : null,
          body: search.mode === "body" ? search.body : null,
          text: search.text,
          filters: search.filters.filter((f) => f.field),
          fields: search.fields,
          searchInContent: search.searchInContent,
          limit: PAGE_SIZE,
          offset: from,
        });
        setResult(found);
        setOffset(from);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    },
    [getToken, connectionId, ready, search],
  );

  const columns = result?.columns ?? [];
  const rows = result?.rows ?? [];
  const total = result?.total ?? null;
  const hasMore = rows.length === PAGE_SIZE && (total === null || offset + rows.length < total);

  return (
    <Stack gap="md">
      <Paper withBorder radius="md" p="md">
        <Stack gap="sm">
          <CaraSearchBuilder
            connectionId={connectionId}
            value={search}
            onChange={(patch) => {
              setSearch((current) => ({ ...current, ...patch }));
              setResult(null);
            }}
          />
          <Group justify="flex-end">
            <Button
              leftSection={<IconSearch size={16} />}
              onClick={() => void run(0)}
              loading={loading}
              disabled={!ready}
            >
              Search
            </Button>
          </Group>
        </Stack>
      </Paper>

      {error && (
        <Alert variant="light" color="red" title="Search failed">
          {error}
        </Alert>
      )}

      {rows.length > 0 && (
        <Group justify="space-between">
          <Group gap="xs">
            <Text size="sm" c="dimmed">
              {total === null ? `${rows.length} row(s)` : `${total.toLocaleString()} match(es)`}
              {rows.length > 0 && ` · showing ${offset + 1}–${offset + rows.length}`}
            </Text>
            {result?.truncated && (
              <Badge variant="light" color="yellow">
                More matched than shown
              </Badge>
            )}
          </Group>
          <Group gap="xs">
            <Button
              variant="default"
              size="xs"
              leftSection={<IconChevronLeft size={16} />}
              disabled={offset === 0 || loading}
              onClick={() => void run(Math.max(0, offset - PAGE_SIZE))}
            >
              Previous
            </Button>
            <Button
              variant="default"
              size="xs"
              rightSection={<IconChevronRight size={16} />}
              disabled={!hasMore || loading}
              onClick={() => void run(offset + PAGE_SIZE)}
            >
              Next
            </Button>
            <Button
              variant="default"
              size="xs"
              leftSection={<IconTableExport size={16} />}
              onClick={() => downloadCsv("cara-results.csv", rows)}
            >
              Export CSV
            </Button>
          </Group>
        </Group>
      )}

      <Paper withBorder radius="md" p={0}>
        {loading ? (
          <Group justify="center" p="xl">
            <Loader size="sm" />
          </Group>
        ) : rows.length === 0 ? (
          <Text c="dimmed" p="xl" ta="center">
            {result ? "No records matched." : "Pick an object type above and press Search."}
          </Text>
        ) : (
          <Table.ScrollContainer minWidth={400}>
            <Table verticalSpacing="sm" striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  {columns.map((c) => (
                    <Table.Th key={c}>{c}</Table.Th>
                  ))}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((row, index) => (
                  <Table.Tr key={index}>
                    {columns.map((c) => (
                      <Table.Td key={c}>{cell(row[c])}</Table.Td>
                    ))}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Paper>
    </Stack>
  );
}
